import { faDoorOpen } from '@fortawesome/free-solid-svg-icons/faDoorOpen';
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import { faTachometer } from '@fortawesome/free-solid-svg-icons/faTachometer';
import { faUserShield } from '@fortawesome/free-solid-svg-icons/faUserShield';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import React, { useContext } from 'react';

import { useAuth } from '../../../../context/AuthContext';
import { UserRole } from '../../../../types/user';
import { getIDTokenData } from '../../../../utils/tokenUtils';

import { MenuContext } from './MenuContext';
import { useHamburgerMenuStyles } from './menuStyles/hamburgerMenuStyles';
import { Account } from './shared/Constants';
import { HeaderMenuItem } from './shared/HeaderMenuItem';
import { getIcon } from './shared/IconUtils';
import { MenuSection } from './shared/MenuSection';
import SignUp from './SignUp';
import HeaderEnum from './topLevelMenu/headerEnum';

// HA-287: Roles allowed to see "Account Management" in the
// hamburger menu. Same set as AccountMenu.tsx — keep the two
// surfaces in lockstep so the role-aware filter is consistent.
const PRACTITIONER_LIKE_ROLES: ReadonlyArray<UserRole> = [
  UserRole.Practitioner,
  UserRole.PracticeAdmin,
  UserRole.SystemAdmin,
];

export function HamburgerMenuItems() {
  const { menuConfig } = useContext(MenuContext);
  const theme = useTheme();
  const styles = useHamburgerMenuStyles(theme);
  const idToken = getIDTokenData();
  const { currentUser } = useAuth();

  if (JSON.stringify(idToken).length <= 2) {
    return <SignUp />;
  }

  // HA-287: fail-closed — undefined currentUser (mid-bootstrap)
  // treated as non-practitioner so the link doesn't flash
  // before AuthContext resolves.
  const showAccountManagement = !!currentUser && PRACTITIONER_LIKE_ROLES.includes(currentUser.role);
  // HA-446: the admin console, for SystemAdmins only (same fail-closed rule).
  const showAdminConsole = currentUser?.role === UserRole.SystemAdmin;

  const userFullName = idToken?.email ?? menuConfig?.UserFullName ?? '';

  return (
    <>
      <MenuSection className={`${styles.userName}`}>{userFullName}</MenuSection>
      <Divider variant="middle" />
      {showAccountManagement && (
        <HeaderMenuItem
          className={`main-action ${styles.mainAction}`}
          icon={getIcon(faUsers)}
          relativePath={Account[HeaderEnum.AccountManagement]}
          title={HeaderEnum.AccountManagement}
        />
      )}
      {showAdminConsole && (
        <HeaderMenuItem
          className={`main-action ${styles.mainAction}`}
          icon={getIcon(faUserShield)}
          relativePath={Account[HeaderEnum.AdminConsole]}
          title={HeaderEnum.AdminConsole}
        />
      )}
      <HeaderMenuItem
        className={`main-action ${styles.mainAction}`}
        icon={getIcon(faInfo)}
        relativePath={Account[HeaderEnum.About]}
        title={HeaderEnum.About}
      />
      <HeaderMenuItem
        className={`main-action ${styles.mainAction}`}
        icon={getIcon(faPhone)}
        relativePath={Account[HeaderEnum.Contact]}
        title={HeaderEnum.Contact}
      />
      <HeaderMenuItem
        className={`main-action ${styles.mainAction}`}
        icon={getIcon(faTachometer)}
        relativePath={Account[HeaderEnum.Dashboard]}
        title={HeaderEnum.Dashboard}
      />
      <HeaderMenuItem
        className={`main-action ${styles.mainAction}`}
        icon={getIcon(faPaperPlane)}
        relativePath={Account[HeaderEnum.Services]}
        title={HeaderEnum.Services}
      />
      <HeaderMenuItem
        className={`main-action ${styles.mainAction}`}
        icon={getIcon(faShieldHalved)}
        relativePath={Account[HeaderEnum.TwoFactorAuthentication]}
        title={HeaderEnum.TwoFactorAuthentication}
      />
      <HeaderMenuItem
        className={`main-action ${styles.mainAction} ${styles.signOut}`}
        icon={getIcon(faDoorOpen)}
        relativePath={Account[HeaderEnum.SignOut]}
        title={HeaderEnum.SignOut}
      />
    </>
  );
}
